// WHAT GOES INACTIVE WITH IT — the words (owner, 2026-09-25 —
// docs/decisions.md D-175).
//
// *"What makes an Account 'in use'? Definitionally, I would only ever change an
// Account to Inactive from being 'in use.'"* Making an Account, a Department or
// a Code Inactive is never refused for being in use any more. The server makes
// what depends on it Inactive in the same write, and the confirmation says
// exactly what, before anything changes: a list per kind, and a button that
// counts what it does.
//
// Which records go is the server's answer (`GET /accounts/{id}/inactive-preview`
// and its Department and Code twins, planned by
// `backend/src/domain/inactiveCascade.ts`, which the mock imports too). This
// file only words it.

import type { CodingRule, InactiveCascade } from './types'
import { countOf, sentenceList } from './format'

type InactiveNoun = 'Code' | 'Account' | 'Department'

/** How many of each kind go Inactive along with the subject. */
interface CascadeCounts {
  codes: number
  departments: number
  accounts: number
  codingRules: number
}

export function cascadeIsEmpty(cascade: InactiveCascade): boolean {
  return (
    cascade.codes.length === 0 &&
    cascade.departments.length === 0 &&
    cascade.accounts.length === 0 &&
    cascade.coding_rules.length === 0
  )
}

export function cascadeCounts(cascade: InactiveCascade): CascadeCounts {
  return {
    codes: cascade.codes.length,
    departments: cascade.departments.length,
    accounts: cascade.accounts.length,
    codingRules: cascade.coding_rules.length,
  }
}

/**
 * THE CONFIRM BUTTON, counting what it does: *Make Account + 3 Codes + 1
 * Coding Rule Inactive*, or *Make Account Inactive* when nothing goes with it.
 * One line, whatever the length (`confirmButton`'s nowrap in the dialog).
 */
export function makeInactiveLabel(noun: InactiveNoun | string, counts: Partial<CascadeCounts>): string {
  const parts: string[] = [noun]
  if (counts.codes) parts.push(countOf(counts.codes, 'Code'))
  if (counts.departments) parts.push(countOf(counts.departments, 'Department'))
  if (counts.accounts) parts.push(countOf(counts.accounts, 'Account'))
  if (counts.codingRules) parts.push(countOf(counts.codingRules, 'Coding Rule'))
  return `Make ${parts.join(' + ')} Inactive`
}

/** What the list prints where a record's name is blank or not known. */
export const UNNAMED = '(unnamed)'

/**
 * A half or a Code as the dialog names it: BOTH, never just the ID (owner,
 * 2026-09-25 — docs/decisions.md D-182). *Trash Removal (73-675)*, or
 * *(unnamed) (73-675)* when the name is blank or not known, so a row never
 * reads as an empty name.
 */
export function nameWithId(name: string | null | undefined, id: string): string {
  return `${name?.trim() || UNNAMED} (${id})`
}

/** Where the list looks up a half's name. Either may answer nothing. */
interface HalfNames {
  account: (id: string) => string | null | undefined
  department: (id: string) => string | null | undefined
}

/**
 * A Coding Rule as the list shows it: the vendor, then each half the rule names
 * as `Name (ID)`, account first — *NAYLOR LLC → Trash Removal (73-675) / Plant
 * Operations & Maintenance (689559)* — and what it matches under it (owner,
 * 2026-09-25, D-182).
 */
export function codingRuleListItem(
  rule: Pick<CodingRule, 'vendor_name' | 'vendor_id' | 'description_scope'> &
    Partial<Pick<CodingRule, 'account_id' | 'department_id'>>,
  names?: HalfNames,
): { primary: string; secondary: string } {
  const scope = rule.description_scope?.trim()
  const vendor = rule.vendor_name || rule.vendor_id || 'Any vendor'
  const halves = [
    rule.account_id ? nameWithId(names?.account(rule.account_id), rule.account_id) : null,
    rule.department_id ? nameWithId(names?.department(rule.department_id), rule.department_id) : null,
  ].filter((half): half is string => half !== null)
  return {
    primary: halves.length > 0 ? `${vendor} → ${halves.join(' / ')}` : vendor,
    secondary: scope ? `“${scope}”` : 'any description',
  }
}

/**
 * The names the dialog resolves a Coding Rule's halves from: the cascade's own
 * Departments and Accounts first (they are records going Inactive with it),
 * then the half masters the tab has loaded.
 */
export function cascadeHalfNames(
  cascade: Pick<InactiveCascade, 'accounts' | 'departments'>,
  accountNames: ReadonlyMap<string, string>,
  departmentNames: ReadonlyMap<string, string>,
): HalfNames {
  const accounts = new Map(cascade.accounts.map((a) => [a.account_id, a.account_name] as const))
  const departments = new Map(cascade.departments.map((d) => [d.department_id, d.department_name] as const))
  return {
    account: (id) => accounts.get(id) || accountNames.get(id),
    department: (id) => departments.get(id) || departmentNames.get(id),
  }
}

/** The one sentence about invoices, the same in every dialog of the family. */
export const IN_PROGRESS_NOTE = 'Invoices already in progress keep their coding.'

/** A Code made Active again brings back an Account or Department it was the
    last Active Code of (a derived half follows its Codes, D-69), never a
    Coding Rule. */
export const CODE_REACTIVATION_NOTE =
  'Making the Code Active again brings back an Account or Department it was the last Code of, but not its Coding Rules.'

/** Making a half Active again brings nothing back (owner, 2026-09-25). */
export function reactivationNote(noun: InactiveNoun | 'Accounts' | 'Departments' | 'Codes'): string {
  return `Making the ${noun} Active again does not make these Active again.`
}

/** The toast after it lands: *The account is now Inactive, with 3 Codes and 1 Coding Rule.* */
export function inactiveToast(noun: InactiveNoun, counts: Partial<CascadeCounts>): string {
  const withIt = [
    counts.codes ? countOf(counts.codes, 'Code') : null,
    counts.departments ? countOf(counts.departments, 'Department') : null,
    counts.accounts ? countOf(counts.accounts, 'Account') : null,
    counts.codingRules ? countOf(counts.codingRules, 'Coding Rule') : null,
  ].filter((part): part is string => part !== null)
  const head = `The ${noun.toLowerCase()} is now Inactive`
  if (withIt.length === 0) return `${head}.`
  // `sentenceList`, the app's one joiner: three or more take the Oxford comma.
  return `${head}, with ${sentenceList(withIt)}.`
}

/**
 * SEVERAL AT ONCE — the bulk strip's Mark Inactive on the Codes, Accounts or
 * Departments view (D-175, D-180). Each selected half is its own write, and each write takes
 * its own cascade, so the list is the union of their previews, plus one thing
 * no single preview can see: an other half whose Active Codes are spread over
 * SEVERAL of the selected halves, so that it loses its last one only once all
 * of them have gone. `combinedKnockOns` finds those from the chart the tab
 * already holds; their Coding Rules come from their own previews.
 */
export function mergeCascades(cascades: readonly InactiveCascade[]): InactiveCascade {
  const byKey = <T,>(rows: T[], key: (row: T) => string): T[] => [...new Map(rows.map((r) => [key(r), r])).values()]
  return {
    codes: byKey(cascades.flatMap((c) => c.codes), (c) => c.code_id).sort((a, b) => a.code_id.localeCompare(b.code_id)),
    departments: byKey(cascades.flatMap((c) => c.departments), (d) => d.department_id).sort((a, b) =>
      a.department_id.localeCompare(b.department_id),
    ),
    accounts: byKey(cascades.flatMap((c) => c.accounts), (a) => a.account_id).sort((a, b) =>
      a.account_id.localeCompare(b.account_id),
    ),
    coding_rules: byKey(cascades.flatMap((c) => c.coding_rules), (r) => r.id),
  }
}

/**
 * Halves of kind `half` that lose their last Active Code only to the union of
 * `codeIds` — the knock-ons no single preview can see.
 */
export function combinedKnockOns(
  half: 'account' | 'department',
  chart: readonly { code_id: string; is_active: boolean }[],
  halves: readonly { id: string; is_active: boolean; origin?: string | null }[],
  codeIds: ReadonlySet<string>,
): string[] {
  // `NNNNNN-NN-NNN`: the department is the leading six digits, the account the rest.
  const halfOf = (codeId: string) => (half === 'department' ? codeId.slice(0, 6) : codeId.slice(7))
  const candidates = new Set([...codeIds].map(halfOf))
  return halves
    .filter((row) => candidates.has(row.id) && row.is_active && row.origin !== 'standalone')
    .filter((row) => !chart.some((c) => c.is_active && !codeIds.has(c.code_id) && halfOf(c.code_id) === row.id))
    .map((row) => row.id)
    .sort()
}
